import { io, Socket } from 'socket.io-client';
import { useAuth0 } from '@auth0/auth0-react';
import * as uuid from 'uuid';
import { getProject } from '@client/store/project';
import { useSelectedOrganizationName } from '@client/store/organization';
import { useCallback, useEffect, useRef } from 'react';
import { Project } from '@shared/models/project.model';
import {
  AiConversation,
  AiConversationMetadata,
} from '@shared/models/ai-chat.model';
import { useConfig } from '../providers/ConfigProvider';
import { useCommentsStore } from '@client/store/comment/comment.store';
import { IComment } from '@shared/models/comment.model';
import { useRecipesStore } from '@client/store/recipe';
import { IProduct } from '@shared/models/product.model';
import {
  UpdateClientAction,
  UpdateClientData,
} from '@client/store/store.model';
import { required } from '@shared/helpers/function.helpers';
import { SocketEventName } from '@shared/helpers/socket.helpers';
import { Recipe } from '@shared/models/recipe.model';
import { isEqualIds } from '@shared/helpers/utils.helpers';
import { useAiChatStore } from '@features/ai-chat/ai-chat.store';
import { useProjectStateStore } from '@client/store/project/project.store';
import {
  markOpenProjectRevisionUnverified,
  projectSocketDropInvalidatesRevision,
} from '@client/store/project/project-revision-sync.helpers';
import { useProductStore } from '@client/store/product/product.store';

type SocketListenerUtils = () => [() => Promise<void>, () => void];

const handleResource =
  <T>(
    event:
      | UpdateClientAction.Add
      | UpdateClientAction.Update
      | UpdateClientAction.Remove,
    cb: (args: UpdateClientData<T>) => void,
  ) =>
  (resource: T, initiatorSessionId: string) => {
    const sessionId = window.sessionStorage.getItem('id') ?? '';

    if (initiatorSessionId === sessionId || !resource) {
      return;
    }
    event === UpdateClientAction.Remove
      ? cb({ action: event, itemOrId: resource as string | number })
      : cb({ action: event, itemOrId: resource });
  };

const reloadOnProjectDelete = ({
  itemOrId,
}: UpdateClientData<Project>): void => {
  if (isEqualIds(getProject(), itemOrId)) {
    window.location.reload();
  }
};

export const useSocketListeners: SocketListenerUtils = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [config] = useConfig();

  const selectedOrganization = useSelectedOrganizationName();
  const socketRef = useRef<Socket | undefined>(undefined);

  useEffect(() => window.sessionStorage.setItem('id', uuid.v4()), []);

  const clearSocketListeners = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.removeAllListeners();
    socket.disconnect();
    socketRef.current = undefined;
  }, [socketRef]);

  const addSocketListeners = useCallback(async () => {
    clearSocketListeners();
    const token = await getAccessTokenSilently();
    const sessionId = window.sessionStorage.getItem('id') ?? '';
    const socket = io(config.apiUrl, {
      auth: {
        token: `Bearer ${token}`,
      },
      extraHeaders: {
        AppVersion: config.version,
      },
      query: {
        organization: required(
          selectedOrganization,
          'No organization selected',
        ),
        sessionId,
        projectId: getProject().id,
      },
      transports: ['websocket'],
    });
    socketRef.current = socket;
    socket.on('disconnect', (reason: string) => {
      if (!projectSocketDropInvalidatesRevision(reason)) return;
      markOpenProjectRevisionUnverified();
    });

    // Read store actions at event time via getState() to avoid unstable references
    const projectStore = () => useProjectStateStore.getState();
    const productStore = () => useProductStore.getState();
    const commentStore = () => useCommentsStore.getState();
    const recipeStore = () => useRecipesStore.getState();

    socket.on(
      SocketEventName.ProjectCreate,
      handleResource<Project>(UpdateClientAction.Add, (args) =>
        projectStore().updateProjectLocally(args),
      ),
    );

    socket.on(
      SocketEventName.ProjectUpdate,
      handleResource<Project>(UpdateClientAction.Update, (args) =>
        projectStore().updateProjectLocally(args),
      ),
    );
    socket.on(
      SocketEventName.ProjectDelete,
      handleResource<Project>(UpdateClientAction.Remove, reloadOnProjectDelete),
    );

    socket.on(
      SocketEventName.RecipeCreate,
      handleResource<Recipe>(UpdateClientAction.Add, (args) =>
        recipeStore().updateRecipesLocally(args),
      ),
    );
    socket.on(
      SocketEventName.RecipeUpdate,
      handleResource<Recipe>(UpdateClientAction.Update, (args) =>
        recipeStore().updateRecipesLocally(args),
      ),
    );
    socket.on(
      // A recipe delete is a soft-delete: update in place (deleted_at set) so the
      // recipe stays in the store and references keep resolving. Pickers hide it.
      SocketEventName.RecipeDelete,
      handleResource<Recipe>(UpdateClientAction.Update, (args) =>
        recipeStore().updateRecipesLocally(args),
      ),
    );

    socket.on(
      SocketEventName.ProductCreate,
      handleResource<IProduct>(UpdateClientAction.Add, (args) =>
        productStore().updateProductsLocally(args),
      ),
    );
    socket.on(
      SocketEventName.ProductUpdate,
      handleResource<IProduct>(UpdateClientAction.Update, (args) =>
        productStore().updateProductsLocally(args),
      ),
    );
    socket.on(
      SocketEventName.ProductDelete,
      handleResource<IProduct>(UpdateClientAction.Remove, (args) =>
        productStore().updateProductsLocally(args),
      ),
    );

    socket.on(SocketEventName.CommentGroupUpdate, (comments: IComment[]) =>
      commentStore().applyCommentGroup(comments),
    );
    socket.on(
      SocketEventName.CommentCreate,
      handleResource<IComment>(UpdateClientAction.Add, (args) =>
        commentStore().updateCommentsLocally(args),
      ),
    );
    socket.on(
      SocketEventName.CommentUpdate,
      handleResource<IComment>(UpdateClientAction.Update, (args) =>
        commentStore().updateCommentsLocally(args),
      ),
    );
    socket.on(
      SocketEventName.CommentDelete,
      handleResource<IComment>(UpdateClientAction.Remove, (args) =>
        commentStore().updateCommentsLocally(args),
      ),
    );

    socket.on(
      SocketEventName.AiConversationUpdate,
      (conversation: AiConversation) => {
        useAiChatStore.getState().upsertConversation(conversation);
      },
    );
    socket.on(
      SocketEventName.AiConversationMetadataUpdate,
      (meta: AiConversationMetadata) => {
        useAiChatStore.getState().applyConversationMetadata(meta);
      },
    );
  }, [
    clearSocketListeners,
    getAccessTokenSilently,
    config.apiUrl,
    config.version,
    selectedOrganization,
  ]);

  return [addSocketListeners, clearSocketListeners];
};
